// Phase 11 — CreateCasePage.
//
// Backend contract: POST /api/v1/cases (existing). Body = CreateCaseRequest.
// Backend gate: SECTION_HEAD of (branchId, departmentId), OR ADMIN_CLERK with
// delegated CREATE_CASE.
//
// UX rules:
//   - Branches dropdown: limited to the user's own active SECTION_HEAD/ADMIN_CLERK
//     memberships (the ONLY (branch, dept) pairs the backend will accept). This
//     avoids showing options that will be rejected on submit.
//   - Departments dropdown: limited to the user's memberships in the chosen branch.
//   - Courts dropdown: filtered by branchId + the chosen department's TYPE so we
//     never offer a court that fails `OrganizationService.validateConsistency`.
//   - Postponement reason in this phase: free text (D-020). The "managed list"
//     (postponement_reasons reference table) is consumed by the rollover/finalize
//     flow only; the create-case payload still uses a free-text VARCHAR per D-020.
//     This is documented as gap "Phase 11 / lookups" — backend acceptance is
//     unaffected because the column is free text at this layer.
//   - Free-text fields trimmed; numeric coercion via zod.
//   - Errors from backend bubbled via extractApiErrorMessage.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { createCase } from './api';
import { useAuth } from '@/features/auth/AuthContext';
import { canCreateCase } from '@/features/auth/permissions';
import { listBranches, listDepartments, listCourts } from '@/shared/api/lookups';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select, Textarea } from '@/shared/ui/FormFields';
import { Spinner } from '@/shared/ui/Spinner';
import { extractApiErrorMessage } from '@/shared/lib/apiError';
import {
  COURT_TYPE_LABEL_AR,
  COURT_TYPE_OPTIONS,
  PUBLIC_ENTITY_POSITION_LABEL_AR,
  STAGE_TYPE_LABEL_AR,
  type CourtType,
  type CreateCaseRequest,
  type DepartmentType,
  type PublicEntityPosition,
  type StageType,
} from '@/shared/types/domain';

// Customer feedback round-2 (PR-15a):
//   - Year is auto-derived from `originalRegistrationDate` (no separate input).
//     Eliminates the user-reported "بنية الطلب غير صحيحة" caused by NaN years.
//   - Labels renamed: "اسم الدائرة" → "رقم الغرفة", "رقم أساس المرحلة" → "رقم أساس الدعوى".
const schema = z.object({
  branchId:                z.coerce.number().int().positive('اختر الفرع'),
  departmentId:            z.coerce.number().int().positive('اختر القسم'),
  courtId:                 z.coerce.number().int().positive('اختر المحكمة'),
  chamberName:             z.string().trim().max(128).optional().or(z.literal('')),
  publicEntityName:        z.string().trim().min(1, 'مطلوب').max(200),
  publicEntityPosition:    z.enum(['PLAINTIFF', 'DEFENDANT']),
  opponentName:            z.string().trim().min(1, 'مطلوب').max(200),
  originalBasisNumber:     z.string().trim().min(1, 'مطلوب').max(64),
  originalRegistrationDate: z.string().min(1, 'مطلوب')        // yyyy-MM-dd
                              .regex(/^\d{4}-\d{2}-\d{2}$/, 'تاريخ غير صالح'),
  stageType:               z.enum(['CONCILIATION', 'FIRST_INSTANCE', 'APPEAL', 'SINGLE_INSTANCE']),
  /** Customer feedback round-2: نوع المحكمة. */
  courtType: z.enum([
    'URGENT', 'MARITIME', 'BANKING', 'LABOR',
    'GENERAL', 'INSURANCE', 'CUSTOMS', 'ADMINISTRATIVE',
  ]),
  firstHearingDate:        z.string().min(1, 'مطلوب'),       // yyyy-MM-dd
  firstPostponementReason: z.string().trim().min(1, 'الرجاء تعبئة سبب التأجيل الأول').max(200),
  // Phase 2 (Damascus registers) — optional fields, only shown for CASSATION.
  circulationNumber:       z.string().trim().max(64).optional().or(z.literal('')),
  capacity:                z.string().trim().max(128).optional().or(z.literal('')),
  appealResult:            z.string().trim().max(200).optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

const POSITION_OPTIONS: PublicEntityPosition[] = ['PLAINTIFF', 'DEFENDANT'];
const STAGE_OPTIONS: StageType[] = ['CONCILIATION', 'FIRST_INSTANCE', 'APPEAL'];

/**
 * Map the original 4 stage types → DepartmentType for the courts filter.
 * The new SINGLE_INSTANCE registers (CASSATION / ADMINISTRATIVE_JUDICIARY)
 * resolve the courts filter directly from the chosen department's TYPE, so
 * they are not part of this StageType→DepartmentType map.
 */
const STAGE_TO_DEPT_TYPE: Record<StageType, DepartmentType> = {
  CONCILIATION:    'CONCILIATION',
  FIRST_INSTANCE:  'FIRST_INSTANCE',
  APPEAL:          'APPEAL',
  SINGLE_INSTANCE: 'CASSATION',
};

export function CreateCasePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const allowed = canCreateCase(user);

  const branchesQ = useQuery({ queryKey: ['lookup', 'branches'], queryFn: listBranches, enabled: allowed });

  const allowedBranchIds = useMemo(() => {
    if (!user) return new Set<number>();
    return new Set(
      user.departmentMemberships
        .filter((m) => m.active && (m.membershipType === 'SECTION_HEAD' || m.membershipType === 'ADMIN_CLERK'))
        .map((m) => m.branchId),
    );
  }, [user]);

  const visibleBranches = useMemo(
    () => (branchesQ.data ?? []).filter((b) => b.active && allowedBranchIds.has(b.id)),
    [branchesQ.data, allowedBranchIds],
  );

  // ── Customer feedback round-2: section-head lock ────────────────
  // If the user is a SECTION_HEAD with exactly ONE active membership,
  // the case create form auto-fills branch + department + stage-type
  // and hides those pickers. Per the customer:
  //   "Head of FI department creates files only as FI — no department or
  //    stage-type choice is shown."
  // ADMIN_CLERK with delegations (or any user with multiple section-head
  // memberships) keeps the editable pickers limited to their reach.
  const lockedSectionHeadMembership = useMemo(() => {
    if (!user) return null;
    const sh = user.departmentMemberships.filter(
      (m) => m.active && m.membershipType === 'SECTION_HEAD' && m.departmentId != null,
    );
    return sh.length === 1 ? sh[0] : null;
  }, [user]);

  const {
    register, handleSubmit, watch, setValue, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      publicEntityPosition: 'DEFENDANT',
      stageType: 'FIRST_INSTANCE',
      // Customer's spec lists "عادي" as the standard option — use it as the default.
      courtType: 'GENERAL',
    },
  });

  const branchId    = watch('branchId');
  const stageType   = watch('stageType');
  const departmentId = watch('departmentId');

  const departmentsQ = useQuery({
    queryKey: ['lookup', 'departments', branchId],
    queryFn: () => listDepartments(Number(branchId)),
    enabled: allowed && !!branchId,
  });

  // Phase 2 (Damascus registers) — resolve the selected department's TYPE.
  // The 3 new Damascus departments (CASSATION / ADMINISTRATIVE_JUDICIARY /
  // EXTERNAL_DISPUTES) have no promotion ladder: they file as SINGLE_INSTANCE,
  // and the courts filter follows the department type directly (not the
  // StageType→DepartmentType map used by the original 4 types).
  const selectedDept = useMemo(
    () => (departmentsQ.data ?? []).find((d) => d.id === Number(departmentId)) ?? null,
    [departmentsQ.data, departmentId],
  );
  const isSingleInstanceDept =
    selectedDept?.type === 'CASSATION'
    || selectedDept?.type === 'ADMINISTRATIVE_JUDICIARY'
    || selectedDept?.type === 'EXTERNAL_DISPUTES';
  const isCassationDept = selectedDept?.type === 'CASSATION';

  // Resolve the locked dept's row so we can read its type → matching stage-type.
  const lockedDept = useMemo(() => {
    if (!lockedSectionHeadMembership) return null;
    return (departmentsQ.data ?? []).find(
      (d) => d.id === lockedSectionHeadMembership.departmentId,
    ) ?? null;
  }, [departmentsQ.data, lockedSectionHeadMembership]);

  // CONCILIATION → CONCILIATION, FIRST_INSTANCE → FIRST_INSTANCE,
  // APPEAL → APPEAL. EXECUTION dept heads can't create cases via this form
  // (executions land via promote-to-execution); leave stage as the default.
  const lockedStageType: StageType | null = useMemo(() => {
    if (!lockedDept) return null;
    if (lockedDept.type === 'CONCILIATION')   return 'CONCILIATION';
    if (lockedDept.type === 'FIRST_INSTANCE') return 'FIRST_INSTANCE';
    if (lockedDept.type === 'APPEAL')         return 'APPEAL';
    // Phase 2 (Damascus registers): the 3 new departments have no promotion
    // ladder — they always file as SINGLE_INSTANCE.
    if (lockedDept.type === 'CASSATION'
        || lockedDept.type === 'ADMINISTRATIVE_JUDICIARY'
        || lockedDept.type === 'EXTERNAL_DISPUTES') return 'SINGLE_INSTANCE';
    return null; // EXECUTION
  }, [lockedDept]);

  // Auto-fill the locked branch + department + stage-type. Once these are set,
  // the corresponding pickers are hidden in the JSX below.
  useEffect(() => {
    if (!lockedSectionHeadMembership) return;
    setValue('branchId', lockedSectionHeadMembership.branchId);
  }, [lockedSectionHeadMembership, setValue]);

  useEffect(() => {
    if (!lockedSectionHeadMembership) return;
    setValue('departmentId', lockedSectionHeadMembership.departmentId as number);
  }, [lockedSectionHeadMembership, setValue]);

  useEffect(() => {
    if (!lockedStageType) return;
    setValue('stageType', lockedStageType);
  }, [lockedStageType, setValue]);

  const allowedDeptIds = useMemo(() => {
    if (!user || !branchId) return new Set<number>();
    return new Set(
      user.departmentMemberships
        .filter(
          (m) =>
            m.active &&
            m.branchId === Number(branchId) &&
            (m.membershipType === 'SECTION_HEAD' || m.membershipType === 'ADMIN_CLERK') &&
            m.departmentId != null,
        )
        .map((m) => m.departmentId as number),
    );
  }, [user, branchId]);

  const visibleDepartments = useMemo(
    () => (departmentsQ.data ?? []).filter((d) => d.active && allowedDeptIds.has(d.id)),
    [departmentsQ.data, allowedDeptIds],
  );

  // For the new single-instance departments the courts filter follows the
  // department's own TYPE; otherwise it follows the chosen stage type.
  const departmentTypeForCourts: DepartmentType = isSingleInstanceDept && selectedDept
    ? selectedDept.type
    : STAGE_TO_DEPT_TYPE[stageType ?? 'FIRST_INSTANCE'];

  const courtsQ = useQuery({
    queryKey: ['lookup', 'courts', branchId, departmentTypeForCourts],
    queryFn: () => listCourts({ branchId: Number(branchId), departmentType: departmentTypeForCourts }),
    enabled: allowed && !!branchId,
  });

  // Keep stageType in sync with the selected department: the 3 new Damascus
  // departments always file as SINGLE_INSTANCE (no promotion ladder), so the
  // stage-type picker is hidden for them and the value is forced here.
  useEffect(() => {
    if (isSingleInstanceDept && stageType !== 'SINGLE_INSTANCE') {
      setValue('stageType', 'SINGLE_INSTANCE');
    } else if (!isSingleInstanceDept && stageType === 'SINGLE_INSTANCE') {
      // Switched back to an original dept type — restore a valid default.
      setValue('stageType', 'FIRST_INSTANCE');
    }
  }, [isSingleInstanceDept, stageType, setValue]);

  // Reset court when branch / stage type changes (their valid courts differ).
  useEffect(() => {
    setValue('courtId', 0 as unknown as number);
  }, [branchId, departmentTypeForCourts, setValue]);

  // Reset department when branch changes.
  // Skip when the section-head lock is active: the lock effect above just set
  // departmentId, and running this would overwrite it with 0 — leaving the
  // form invalid with no visible error (the department picker is hidden in
  // the locked block) and silently breaking submit on prod for users like
  // section_fi_dam who have a single SECTION_HEAD membership.
  useEffect(() => {
    if (lockedSectionHeadMembership) return;
    setValue('departmentId', 0 as unknown as number);
  }, [branchId, lockedSectionHeadMembership, setValue]);

  const createMut = useMutation({
    mutationFn: (body: CreateCaseRequest) => createCase(body),
    onSuccess: (lc) => {
      reset();
      navigate(`/cases/${lc.id}`);
    },
    onError: (e) => setServerError(extractApiErrorMessage(e)),
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title="إنشاء دعوى" />
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">
              لا تملك صلاحية إنشاء دعوى. هذا الإجراء متاح لرؤساء الأقسام، أو الموظف الإداري المُفوَّض بصلاحية إنشاء الدعاوى.
            </p>
          </CardBody>
        </Card>
      </>
    );
  }

  // Year derives from registration date — single source of truth (PR-15a).
  const yearFromDate = (iso: string): number => Number.parseInt(iso.slice(0, 4), 10);
  const watchedRegDate = watch('originalRegistrationDate') ?? '';
  const derivedYear = /^\d{4}-\d{2}-\d{2}$/.test(watchedRegDate)
    ? yearFromDate(watchedRegDate)
    : null;

  const onSubmit = (v: FormValues) => {
    setServerError(null);
    const computedYear = yearFromDate(v.originalRegistrationDate);
    const body: CreateCaseRequest = {
      publicEntityName: v.publicEntityName,
      publicEntityPosition: v.publicEntityPosition,
      opponentName: v.opponentName,
      originalBasisNumber: v.originalBasisNumber,
      basisYear: computedYear,
      originalRegistrationDate: v.originalRegistrationDate,
      branchId: Number(v.branchId),
      departmentId: Number(v.departmentId),
      courtId: Number(v.courtId),
      chamberName: v.chamberName ? v.chamberName : null,
      courtType: v.courtType as CourtType,
      stageType: v.stageType,
      stageBasisNumber: v.originalBasisNumber,
      stageYear: computedYear,
      firstHearingDate: v.firstHearingDate,
      firstPostponementReason: v.firstPostponementReason,
      // Phase 2 (Damascus registers) — optional; only filled for CASSATION.
      circulationNumber: v.circulationNumber ? v.circulationNumber : null,
      capacity: v.capacity ? v.capacity : null,
      appealResult: v.appealResult ? v.appealResult : null,
    };
    createMut.mutate(body);
  };

  return (
    <>
      <PageHeader
        title="إنشاء دعوى"
        subtitle="القيد التأسيسي للدعوى. الفرع/القسم محصور بعضوياتك الفعّالة."
      />

      {serverError && (
        <div role="alert" className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {serverError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 lg:grid-cols-2" noValidate>
        <Card>
          <CardHeader><CardTitle>الموقع التنظيمي</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            {lockedSectionHeadMembership && lockedDept && lockedStageType ? (
              // Customer feedback round-2: section-head with a single membership.
              // Branch / department / stage-type are locked to that membership and
              // shown as read-only info — no choice presented.
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <p className="mb-1 text-xs text-slate-500">
                  نطاقك (مقيَّد بقسمك — لا يمكن تغييره):
                </p>
                <ul className="list-disc space-y-0.5 ps-5 text-slate-700">
                  <li>
                    <span className="text-slate-500">الفرع:</span>{' '}
                    <span className="font-medium">
                      {(branchesQ.data ?? []).find((b) => b.id === lockedSectionHeadMembership.branchId)?.nameAr
                        ?? `#${lockedSectionHeadMembership.branchId}`}
                    </span>
                  </li>
                  <li>
                    <span className="text-slate-500">القسم:</span>{' '}
                    <span className="font-medium">{lockedDept.nameAr}</span>
                  </li>
                  <li>
                    <span className="text-slate-500">نوع المرحلة:</span>{' '}
                    <span className="font-medium">{STAGE_TYPE_LABEL_AR[lockedStageType]}</span>
                  </li>
                </ul>
              </div>
            ) : (
              <>
                <Field label="الفرع" error={errors.branchId?.message}>
                  <Select {...register('branchId')} disabled={branchesQ.isLoading}>
                    <option value="">— اختر —</option>
                    {visibleBranches.map((b) => (
                      <option key={b.id} value={b.id}>{b.nameAr}</option>
                    ))}
                  </Select>
                  {visibleBranches.length === 0 && branchesQ.isFetched && (
                    <p className="mt-1 text-xs text-amber-600">
                      لا توجد فروع متاحة لك (تحتاج عضوية SECTION_HEAD أو ADMIN_CLERK).
                    </p>
                  )}
                </Field>

                <Field label="القسم" error={errors.departmentId?.message}>
                  <Select {...register('departmentId')} disabled={!branchId || departmentsQ.isLoading}>
                    <option value="">— اختر —</option>
                    {visibleDepartments.map((d) => (
                      <option key={d.id} value={d.id}>{d.nameAr}</option>
                    ))}
                  </Select>
                </Field>

                {/* The 3 new Damascus departments have no promotion ladder —
                    they file as SINGLE_INSTANCE, so no stage-type choice. */}
                {!isSingleInstanceDept && (
                  <Field label="نوع المرحلة" error={errors.stageType?.message}>
                    <Select {...register('stageType')}>
                      {STAGE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{STAGE_TYPE_LABEL_AR[s]}</option>
                      ))}
                    </Select>
                  </Field>
                )}
              </>
            )}

            <Field label={isCassationDept ? 'الغرفة' : 'المحكمة'} error={errors.courtId?.message}>
              <Select {...register('courtId')} disabled={!branchId || courtsQ.isLoading}>
                <option value="">— اختر —</option>
                {(courtsQ.data ?? []).filter((c) => c.active).map((c) => (
                  <option key={c.id} value={c.id}>{c.nameAr}</option>
                ))}
              </Select>
            </Field>

            <Field label="رقم الغرفة (اختياري)" error={errors.chamberName?.message}>
              <Input {...register('chamberName')} placeholder="إن وُجد" />
            </Field>

            {/* Customer feedback round-2: required نوع المحكمة. */}
            <Field label="نوع المحكمة" error={errors.courtType?.message}>
              <Select {...register('courtType')}>
                {COURT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{COURT_TYPE_LABEL_AR[t]}</option>
                ))}
              </Select>
            </Field>

            {/* Phase 2 (Damascus registers) — CASSATION-only extra fields. */}
            {isCassationDept && (
              <>
                <Field label="رقم المتداول (اختياري)" error={errors.circulationNumber?.message}>
                  <Input {...register('circulationNumber')} placeholder="إن وُجد" />
                </Field>
                <Field label="صفتها (اختياري)" error={errors.capacity?.message}>
                  <Input {...register('capacity')} placeholder="مثال: طاعن" />
                </Field>
                <Field label="نتيجة الطعن (اختياري)" error={errors.appealResult?.message}>
                  <Input {...register('appealResult')} placeholder="إن وُجدت" />
                </Field>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>أطراف الدعوى</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <Field label="اسم الجهة العامة" error={errors.publicEntityName?.message}>
              <Input {...register('publicEntityName')} />
            </Field>
            <Field label="صفة الجهة" error={errors.publicEntityPosition?.message}>
              <Select {...register('publicEntityPosition')}>
                {POSITION_OPTIONS.map((p) => (
                  <option key={p} value={p}>{PUBLIC_ENTITY_POSITION_LABEL_AR[p]}</option>
                ))}
              </Select>
            </Field>
            <Field label="اسم الخصم" error={errors.opponentName?.message}>
              <Input {...register('opponentName')} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>الأساس الأصلي</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <Field label="رقم الأساس" error={errors.originalBasisNumber?.message}>
              <Input {...register('originalBasisNumber')} />
            </Field>
            <Field label="تاريخ تسجيل الملف" error={errors.originalRegistrationDate?.message}>
              <Input type="date" {...register('originalRegistrationDate')} />
              <p className="mt-1 text-xs text-slate-500">ثابت لا يُعدَّل بعد الإنشاء.</p>
            </Field>
            <Field label="عام الأساس">
              <Input
                type="text"
                value={derivedYear ?? ''}
                readOnly
                aria-readonly="true"
                placeholder="—"
                className="bg-slate-50 text-slate-600"
              />
              <p className="mt-1 text-xs text-slate-500">يُملأ تلقائيًا من تاريخ تسجيل الملف.</p>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>أساس الدعوى + الجلسة الأولى</CardTitle></CardHeader>
          <CardBody className="space-y-3">
            <Field label="تاريخ الجلسة الأولى" error={errors.firstHearingDate?.message}>
              <Input type="date" {...register('firstHearingDate')} />
            </Field>
            <Field label="سبب التأجيل الأول" error={errors.firstPostponementReason?.message}>
              <Textarea
                rows={2}
                placeholder="اكتب سبب التأجيل (مثال: تبليغ الخصم، استكمال مستندات…)"
                {...register('firstPostponementReason')}
              />
            </Field>
          </CardBody>
        </Card>

        <div className="lg:col-span-2 flex gap-2">
          <Button type="submit" disabled={isSubmitting || createMut.isPending}>
            {createMut.isPending ? <Spinner /> : null}
            <span>قيد الدعوى</span>
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/cases')}>إلغاء</Button>
        </div>
      </form>
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

